import endpoint from "#src/config/endpoint";
import request from "#src/utils/request";
import {
  ICreatePassportRequest,
  IPassport,
  IUpdatePassportRequest,
} from "./types";
import { fakePassports } from "./fakeData";
import { IPaginatedResponse } from "#src/@types/api";
import { PassportActionType, PassportRuleType } from "./enums";

// TODO: remove later
const IS_FAKE = !!process.env.REACT_APP_IS_FAKE;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Get list of passports with optional filtering
 */
export const listPassports = async (
  workspaceId: string,
  params?: {
    type?: PassportRuleType;
    action?: PassportActionType;
    enabled?: string;
    port?: string;
    q?: string;
  }
): Promise<IPaginatedResponse<IPassport>> => {
  if (IS_FAKE) {
    console.log("calling listPassports", workspaceId, params);
    await delay(1000);

    let filteredPassports = [...fakePassports];

    // Apply filters
    if (params) {
      if (params.type) {
        filteredPassports = filteredPassports.filter(
          (p) => p.type === params.type
        );
      }

      if (params.action) {
        filteredPassports = filteredPassports.filter(
          (p) => p.action === params.action
        );
      }

      if (params.enabled !== undefined && params.enabled !== "") {
        filteredPassports = filteredPassports.filter(
          (p) =>
            (p.enabled && params.enabled === "1") ||
            (!p.enabled && params.enabled === "0")
        );
      }

      if (params.port) {
        filteredPassports = filteredPassports.filter((p) =>
          p.ports.some((port) => port.port === params.port)
        );
      }

      if (params.port) {
        filteredPassports = filteredPassports.filter((p) =>
          p.ports.some((port) => port.port === params.port)
        );
      }
    }

    const res: IPaginatedResponse<IPassport> = {
      count: filteredPassports.length,
      results: filteredPassports,
    };
    return res;
  }

  return request.get<never, IPaginatedResponse<IPassport>>(
    `${endpoint.WORKSPACES}/${workspaceId}/passports`,
    { params }
  );
};

/**
 * Create a new passport rule
 */
export const createPassport = async (
  workspaceId: string,
  data: ICreatePassportRequest
): Promise<{ id: string }> => {
  // For development/testing - remove this when implementing the real API
  if (IS_FAKE) {
    console.log("calling createPassport", workspaceId, data);
    await delay(1000);
    return Promise.resolve({ id: fakePassports[0].id });
  }

  return request.post<never, { id: string }>(
    `${endpoint.WORKSPACES}/${workspaceId}/passports`,
    data
  );
};

/**
 * Get a specific passport by ID
 */
export const retrievePassport = async (
  workspaceId: string,
  passportId: string
): Promise<IPassport> => {
  // For development/testing - remove this when implementing the real API
  if (IS_FAKE) {
    console.log("calling retrievePassport", workspaceId, passportId);
    await delay(1000);
    const passport = fakePassports.find((p) => p.id === passportId);
    if (passport) {
      return Promise.resolve(passport);
    }
    return Promise.reject(new Error("Passport not found"));
  }

  return request.get<never, IPassport>(
    `${endpoint.WORKSPACES}/${workspaceId}/passports/${passportId}`
  );
};

/**
 * Update an existing passport
 */
export const updatePassport = async (
  workspaceId: string,
  passportId: string,
  data: IUpdatePassportRequest
): Promise<undefined> => {
  // For development/testing - remove this when implementing the real API
  if (IS_FAKE) {
    console.log("calling updatePassport", workspaceId, passportId, data);
    await delay(1000);
    const passportIndex = fakePassports.findIndex((p) => p.id === passportId);
    if (passportIndex >= 0) {
      return;
    }
    return Promise.reject(new Error("Passport not found"));
  }

  return request.put<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/passports/${passportId}`,
    data
  );
};

/**
 * Delete a passport
 */
export const deletePassport = async (
  workspaceId: string,
  passportId: string
): Promise<undefined> => {
  // For development/testing - remove this when implementing the real API
  if (IS_FAKE) {
    console.log("calling deletePassport", workspaceId, passportId);
    await delay(1000);
    const passportIndex = fakePassports.findIndex((p) => p.id === passportId);
    if (passportIndex >= 0) {
      return;
    }
    return Promise.reject(new Error("Passport not found"));
  }

  return request.delete<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/passports/${passportId}`
  );
};

export const changePassportPriority = async (
  workspaceId: string,
  passportId: string,
  preceedingId: string | null
) => {
  // For development/testing - remove this when implementing the real API
  if (IS_FAKE) {
    console.log(
      "calling changePassportPriority",
      workspaceId,
      passportId,
      preceedingId
    );
    await delay(1000);
    const passportIndex = fakePassports.findIndex((p) => p.id === passportId);
    if (passportIndex >= 0) {
      return;
    }
    return Promise.reject(new Error("Passport not found"));
  }

  return request.post<never, undefined>(
    `${endpoint.WORKSPACES}/${workspaceId}/passports/priority`,
    {
      id: passportId,
      swap_id: preceedingId,
    }
  );
};
